import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { PartnerOrMaster } from 'types/partner';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { observer } from 'mobx-react-lite';
import { sortByString } from 'utilities/dataSorters';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  fullName: string;
};

export const PartnersList = observer(() => {
  useSetPageTitle({ title: 'Partners List', subtitle: '' });
  const ps = useStores().partnersStore;
  const pes = useStores().partnersEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');

  useEffect(() => {
    typeof ps.partners[0] === 'number' && ps.getPartners();
    setSortOrder('ASCENDING');
  }, [ps]);

  useEffect(() => {
    if (typeof ps.partners[0] !== 'number' && 'fullName' in ps.partners[0]) {
      let filteredList = (ps.partners as PartnerOrMaster[]).filter((partner) =>
        partner.fullName.toLowerCase().includes(ps.searchValue.toLowerCase())
      );

      filteredList = sortByString(filteredList, 'fullName', sortOrder);

      ps.filteredList = filteredList;
    }
  }, [ps, ps.searchValue, sortOrder]);

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      ps.filteredList.map((partner) => {
        return {
          id: partner.id,
          fullName: partner.fullName,
        };
      }) as RowsDisplay[],
    [ps.filteredList]
  );

  const deletePartner = useCallback(() => {
    pes.selectedPartner.id && pes.deletePartner(pes.selectedPartner.id);
    setIsModalOn(false);
  }, [pes, setIsModalOn]);

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderSearchbar searchValue={ps.searchValue} onChange={ps.setSearchValue} />
        <HeaderCreateButton buttonTitle='New partner' route={DefaultRoutes.PartnersEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>
                Name <SortOrderButton isActive={true} sortOrder={sortOrder} onSortChange={(sortOrder) => setSortOrder(sortOrder)} />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDisplay.length ? (
              rowsDisplay.map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.PartnersEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className={`row-button ${pes.isLoading ? 'disabled' : ''}`}
                          onClick={() => {
                            pes.selectedPartner.id = row.id;
                            setIsModalOn(true);
                          }}
                          disabled={pes.isLoading}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No partners found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={deletePartner} itemName='partner' />
    </div>
  );
});

export default PartnersList;
